// Shared room-type presets for the indoor calculators (Paint, Drywall, Flooring, Tile).
// Dimensions are typical N. American room sizes in imperial feet: length × width × ceiling.
import type { Locale } from '../i18n/locale'

export interface RoomPreset {
  label: string   // English label (also the stable key used by matchedPreset)
  labelFr: string // fr-CA label shown + stored when the locale is French
  lFt: number
  wFt: number
  hFt: number
}

export const ROOM_PRESETS: RoomPreset[] = [
  { label: 'Living Room',    labelFr: 'Salon',              lFt: 16, wFt: 16, hFt: 9 },
  { label: 'Master Bedroom', labelFr: 'Chambre principale', lFt: 16, wFt: 14, hFt: 9 },
  { label: 'Bedroom',        labelFr: 'Chambre',            lFt: 12, wFt: 11, hFt: 8 },
  { label: "Kids' Bedroom",  labelFr: 'Chambre d’enfant',   lFt: 11, wFt: 10, hFt: 8 },
  { label: 'Kitchen',        labelFr: 'Cuisine',            lFt: 12, wFt: 12, hFt: 9 },
  { label: 'Dining Room',    labelFr: 'Salle à manger',     lFt: 14, wFt: 12, hFt: 9 },
  { label: 'Family Room',    labelFr: 'Salle familiale',    lFt: 18, wFt: 16, hFt: 9 },
  { label: 'Home Office',    labelFr: 'Bureau',             lFt: 11, wFt: 10, hFt: 8 },
  { label: 'Bathroom',       labelFr: 'Salle de bain',      lFt:  8, wFt:  8, hFt: 8 },
  { label: 'Powder Room',    labelFr: 'Salle d’eau',        lFt:  6, wFt:  5, hFt: 8 },
  { label: 'Laundry Room',   labelFr: 'Salle de lavage',    lFt:  8, wFt:  6, hFt: 8 },
  { label: 'Hallway',        labelFr: 'Corridor',           lFt: 12, wFt:  4, hFt: 8 },
  { label: 'Entryway',       labelFr: 'Entrée',             lFt:  8, wFt:  6, hFt: 9 },
  { label: 'Nursery',        labelFr: 'Chambre de bébé',    lFt: 10, wFt: 10, hFt: 8 },
  { label: 'Basement',       labelFr: 'Sous-sol',           lFt: 28, wFt: 20, hFt: 8 },
  { label: 'Garage',         labelFr: 'Garage',             lFt: 20, wFt: 20, hFt: 9 },
]

// Neutral default dimensions for a fresh "Room Type" (unselected) room.
export const CUSTOM_DIMS = { lFt: 12, wFt: 12, hFt: 8 }

// Sentinel <option value> for the "Custom room" entry — distinguishes the
// "user typed a name themselves" case from an unselected dropdown.
export const CUSTOM_ROOM_KEY = '__custom__'

// Convert a preset's imperial-foot dimension into the active unit system.
export function toUnit(ft: number, unit: 'imperial' | 'metric'): number {
  return unit === 'metric' ? Math.round(ft * 0.3048 * 10) / 10 : ft
}

// Display label for a preset in the active locale.
export function presetLabel(p: RoomPreset, locale: Locale): string {
  return locale === 'fr' ? p.labelFr : p.label
}

// The preset's English key matching a given room/area name (in either language),
// or '' (= "Room Type" placeholder / a custom name). Used to sync the dropdown.
export function matchedPreset(name: string | undefined): string {
  return ROOM_PRESETS.find((p) => p.label === name || p.labelFr === name)?.label ?? ''
}

// Pick the dropdown's current <option value> for a given room name:
// - empty/undefined name → '' (the "Select a room" placeholder is selected)
// - matches a preset → the preset's English label
// - non-empty name that doesn't match a preset → CUSTOM_ROOM_KEY
//   (the name <input> shows so the user can edit/see their custom name)
export function dropdownValue(name: string | undefined): string {
  if (!name) return ''
  const matched = matchedPreset(name)
  return matched || CUSTOM_ROOM_KEY
}
